"use strict";

import Global from "/js/core/global.js";
import Metrics from "/js/util/metrics.js";
import Constants from "/js/core/constants.js";
import PrimerMaster from "/js/util/primermaster.js";
import AnimSequencer from "/js/util/anim-sequencer.js";
import UserLessons from "/js/core/user-lessons.js";

/**
 * App-specific utility functions.
 */
export default class AppUtil {

  /**
   * Given a path "crumb" which comes from the lesson json file, 
   * creates the correct asset path to load asset off the web.
   *
   * <p>All paths for lesson assets should be created through here. 
   */
  static makeLessonAssetPath(path) {
    path = encodeURI(path);
    path = path.replace(/'/g, "%27");
    if (path.startsWith("shared/")) {
      const s = path.replace("shared/", Constants.sharedAssetsBasePath); 
      return s;
    }
    if (Global.cmsDevJson) {
      // TODO: Get base path value via queryparam instead of using hardcode.
      const s = `https://cms.10xtest.com/lessons/asset?lessonId=${Global.lessonId}&path=${path}`;
      return s;
    }
    const s = Constants.lessonAssetsBasePath + path;
    return s;
  }

  /**
   * Feature image path is constructed "by convention" (does not come from the json data).
   */
  static makeFeatureImagePath(lessonId) {
    const baseId = lessonId.replace("lesson-", "");
    const path = lessonId + "/assets/feature-" + baseId + ".svg";
    return  AppUtil.makeLessonAssetPath(path) ;
  }

  /**
   * Uses the project's "noTransition" class to be able to set css without triggering an associated 
   * transition. See https://stackoverflow.com/a/16575811.
   */
  static setCssWithoutTransition($element, myFunction) {
    $element.addClass('noTransition');
    myFunction();  // A function that should set the element's css.
    $element[0].offsetHeight;  // This triggers a reflow, flushing the CSS changes.
    $element.removeClass('noTransition');
  }

  /** 
   * Does the app-standard slide-up anim.
   * Assumes $element has css transition enabled for the "top" property.
   */
  static animInFromBottom($el, startPos=null, callback=null) {
    $el.show();
    startPos = startPos || Metrics.cardHeight * 1.5;
    AppUtil.setCssWithoutTransition($el, () => { $el.css('top', startPos); });
    const f = () => $el.css('top', '0%');
    if (callback) {
      AnimSequencer.runOne($el, f, callback);
    } else {
      f();
    }
  }

  /** 
   * Does the app-standard slide-up anim.
   * Assumes $element has css transition enabled for the "top" property.
   */
  static animOutToTop($el, endPos, callback) {
    // Not sure why this is necessary but
    AppUtil.setCssWithoutTransition($el, () => { $el.css('top', "0"); });
    
    endPos = endPos || Metrics.cardHeight * -1.5;

    const f = () => {
      $el.hide();
      if (callback) { callback(); }
    }
    AnimSequencer.runOne($el, () => { $el.css('top', endPos); }, f);
  }

  static sendCloseToParentWindow() {
    try {
      window.top.postMessage({ "name": "overlay-close" }, Global.parentOrigin);
    } catch (err) {
      clw(err)
    }
  }

  static getNextUnfinishedLesson(lessonId) {
    if (!Global.minicourseData) {
      return null;
    }
    const lessonIds = Global.minicourseData.lessonIds;

    const currentIndex = lessonIds.indexOf(lessonId);
    if (currentIndex == -1) {
      return null;
    }
    const startIndex = currentIndex + 1;
    const endIndex = currentIndex + lessonIds.length - 1;
    for (var i = startIndex; i <= endIndex; i++) {
      const index = i % lessonIds.length;
      const id = Global.minicourseData.lessonIds[index];
      if (!UserLessons.isComplete(id)) {
        return id;
      }
    }

    // By convention if all lessons are complete, returns empty string (not null);
    return "";
  }

  static absoluteIndexToStackAndCardIndex(lessonData, absIndex) {
    const stacks = lessonData.stacks;
    if (!stacks) {
      cl("bad data a");
      return [0, 0];
    }
    let counter = 0;
    let i;
    let j;
    for (i = 0; i < stacks.length; i++) {
      const stack = stacks[i];
      const cards = stack.cards;
      if (!cards) {
        cl("bad data b");
        return [0, 0];
      }
      for (j = 0; j < cards.length; j++) {
        if (counter >= absIndex) {
          return [i, j];
        }
        counter++;
      }
    }
    cl("absIndex is too large");
    return [i - 1, j - 1];
  }

  static getNumCards(lessonData) {
    const stacks = lessonData.stacks;
    if (!stacks) {
      cl("bad data a");
      return 0;
    }
    let total = 0;
    stacks.forEach(stack => {
      if (!stack.cards) {
        cl("bad data b");
      } else {
        total += stack.cards.length;
      }
    });
    return total;
  }

  /**
   * Updates the class/es applied to <body>, which may be used to affect layout, properties, and
   * visibility of various elements.
   *
   * TODO: There's a better way of doing this. Eg, App should know the main app states on its own, and should just handle an event that's sent from whereever.
   * TODO: This can probly just go in apptransitionutil now
   *
   * @param mainType "minicourse", "lesson", "done", "recap"
   * @param lessonSubtype "interaction", "gallery"
   */
  static updateBodyClasses(mainType, lessonSubtype) {

    // First remove all of the possible classes from body.
    const $body = $("body");
    $body.removeClass("isMinicourse isLesson isDone isRecap");
    $body.removeClass("isInteraction isGallery");
    $body.removeClass("interactionBgCyan interactionBgGreen interactionBgTeal");  // Caution: hardcoded. // TODO: These css classes should be managed in a separate function.

    switch (mainType) {
      case "minicourse":
        $body.addClass("isMinicourse");
        break;
      case "lesson":
        $body.addClass("isLesson");
        break;
      case "done":
        $body.addClass("isDone");
        break;
      case "recap":
        $body.addClass("isRecap");
        break;
      default:
        cl("updateBodyClasses() - BAD VALUE", mainType);
        break;
    }

    if (mainType === "lesson") {
      switch (lessonSubtype) {
        case "interaction":
          $body.addClass("isInteraction");
          // Also change the bg color
          const cls = PrimerMaster.getInteractionBgColorClassForLessonId(Global.lessonId);
          if (cls) {
            $body.addClass(cls);
          }
          break;
        case "gallery":
          $body.addClass("isGallery");
          break;
      }
    }
  }

  /**
   * For good measure, strips potentially dangerous characters from a user input string.
   */
  static sanitizeUserInput(value) {
    if (!value) {
      return "";
    }
    if (typeof value === "number") {
      return value;
    }
    if (typeof value !== "string") {
      // undefined behavior!
      clw("WARNING: sanitizeUserInput() expected string but got " + value);
      return value;
    }
    // Strip angle brackets and slashes, that's it.
    const chars = [`<`, `>`, `/`, `\\`];
    for (const item of chars) {
      value = value.replace(item, "");
    }
    return value;
  }

  /**
   * Given a container element, returns a "cache array" with information
   * on what child elements have tabindex values.
   * AND, clears the tabindex values.
   */
  static cacheAndClearTabIndexValues($el) {
    const $els = $el.find("[tabindex]");
    const result = [];
    for (const el of $els) {
      const o = {
        el: el,
        tabindex: $(el).attr("tabindex")
      };
      result.push(o);
    }
    // cl('result:', result);
    $els.attr("tabindex", "-1");
    return result;
  }

  /**
   * Restores the tabindex values of elements using the "cache array" created above.
   */
  static restoreTabIndexValues($cacheArray) {
    for (const o of $cacheArray) {
      if (o.el && o.tabindex !== undefined) {
        $(o.el).attr("tabindex", o.tabindex);
      }
    }
  }

  /**
   * Returns the "summary" card from the lesson data. This card is not part of
   * the lesson proper, but is needed for the lesson recap "key takeaways".
   *
   * Must be called before `massageLessonData` (which removes this card).
   */
  static getTakeawaysFromRawLessonData(data) {
    const lastStackCards = data.stacks[data.stacks.length - 1].cards;
    for (let i = lastStackCards.length - 1; i >= 0; i--) {
      const card = lastStackCards[i];
      if (card.layout === "summary") {
        return card;
      }
    }
    return null;
  }

  /**
   * Massages the raw lesson json data coming from the server (removes any legacy
   * cards that may still exist, etc). Same type of logic as is done in the mobile apps.
   */
  static massageLessonData(data) {
    let takeawaysCard;

    // Clean up last stack if necessary
    const lastStackCards = data.stacks[data.stacks.length - 1].cards;
    for (let i = lastStackCards.length - 1; i >= 0; i--) {
      const card = lastStackCards[i];
      if (card.layout === "summary") {
        // This card is used for the "Recap" and is not meant to be seen in the lesson proper.
        takeawaysCard = card;
        lastStackCards.splice(i, 1);
      } else if (card.layout === "end") {
        // Deprecated card type which has never been fully cleaned up in the CMS.
        lastStackCards.splice(i, 1);
      }
    }
    if (lastStackCards.length === 0) {
      data.stacks.pop();
    }
  }
}
